/**
 * Query-key factory helper.
 *
 * Centralizing key construction prevents the classic mistake of invalidating
 * with `['service-requests']` while a query was registered with
 * `['service-requests', 'list']` — they look equivalent but TanStack Query
 * treats them as different roots.
 *
 * Per-app api modules build a typed factory like:
 *
 *   export const serviceRequestKeys = createKeyFactory('service-requests', {
 *     list:   (filters?: { status?: string }) => ['list', filters] as const,
 *     detail: (id: string)                    => ['detail', id]     as const,
 *   });
 *
 *   serviceRequestKeys.all                          // ['service-requests']
 *   serviceRequestKeys.list({ status: 'open' })     // ['service-requests','list',{status:'open'}]
 *   serviceRequestKeys.detail('abc')                // ['service-requests','detail','abc']
 *
 * Then `queryClient.invalidateQueries({ queryKey: serviceRequestKeys.all })`
 * cleanly invalidates everything under the resource.
 */

type SubKey = readonly unknown[];
type SubKeyMap = Record<string, (...args: never[]) => SubKey>;

type ScopedKeys<R extends string, M extends SubKeyMap> = {
  all: readonly [R];
} & {
  [K in keyof M]: (...args: Parameters<M[K]>) => readonly [R, ...ReturnType<M[K]>];
};

export function createKeyFactory<R extends string, M extends SubKeyMap>(
  root: R,
  subKeys: M,
): ScopedKeys<R, M> {
  const factory = { all: [root] as const } as ScopedKeys<R, M>;
  for (const name of Object.keys(subKeys) as Array<keyof M>) {
    const fn = subKeys[name];
    if (fn === undefined) continue;
    (factory as Record<string, unknown>)[name as string] = (...args: unknown[]) =>
      [root, ...fn(...(args as never[]))] as const;
  }
  return factory;
}
